import React, { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeftRight,
  Plus,
  Minus,
  RefreshCw,
  Smartphone,
  Wallet,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const currencies = [
  { code: "USD", name: "US Dollar", flag: "🇺🇸", symbol: "$" },
  { code: "KES", name: "Kenyan Shilling", flag: "🇰🇪", symbol: "KSh" },
  { code: "UGX", name: "Ugandan Shilling", flag: "🇺🇬", symbol: "USh" },
  { code: "TZS", name: "Tanzanian Shilling", flag: "🇹🇿", symbol: "TSh" },
  { code: "RWF", name: "Rwandan Franc", flag: "🇷🇼", symbol: "FRw" },
];

export default function WalletPage() {
  const [wallet, setWallet] = useState(null);
  const [fxRates, setFxRates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // FX Conversion State
  const [fxFrom, setFxFrom] = useState("USD");
  const [fxTo, setFxTo] = useState("KES");
  const [fxAmount, setFxAmount] = useState("");
  const [fxQuote, setFxQuote] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);

  // M-Pesa State
  const [mpesaDialogOpen, setMpesaDialogOpen] = useState(false);
  const [mpesaOperation, setMpesaOperation] = useState("deposit");
  const [mpesaAmount, setMpesaAmount] = useState("");
  const [mpesaCurrency, setMpesaCurrency] = useState("KES");
  const [mpesaPhone, setMpesaPhone] = useState("");
  const [mpesaLoading, setMpesaLoading] = useState(false);
  const [mpesaSuccess, setMpesaSuccess] = useState(false);

  const fetchData = async () => {
    try {
      const [walletRes, ratesRes] = await Promise.all([
        axios.get(`${API}/wallet`, { withCredentials: true }),
        axios.get(`${API}/fx/rates`, { withCredentials: true }),
      ]);
      setWallet(walletRes.data);
      setFxRates(ratesRes.data.rates);
    } catch (error) {
      toast.error("Failed to fetch wallet data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getQuote = async () => {
    if (!fxAmount || parseFloat(fxAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setFxLoading(true);
    try {
      const response = await axios.post(
        `${API}/fx/quote`,
        {
          from_currency: fxFrom,
          to_currency: fxTo,
          amount: parseFloat(fxAmount),
        },
        { withCredentials: true }
      );
      setFxQuote(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to get quote");
    } finally {
      setFxLoading(false);
    }
  };

  const executeConversion = async () => {
    if (!fxQuote) return;

    setFxLoading(true);
    try {
      await axios.post(
        `${API}/fx/convert`,
        {
          from_currency: fxFrom,
          to_currency: fxTo,
          amount: parseFloat(fxAmount),
        },
        { withCredentials: true }
      );
      toast.success("Conversion successful!");
      setFxQuote(null);
      setFxAmount("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Conversion failed");
    } finally {
      setFxLoading(false);
    }
  };

  const handleMpesaTransaction = async () => {
    if (!mpesaAmount || parseFloat(mpesaAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!mpesaPhone) {
      toast.error("Please enter phone number");
      return;
    }

    setMpesaLoading(true);
    setMpesaSuccess(false);
    
    try {
      const endpoint = mpesaOperation === "deposit" ? "/mpesa/stk-push" : "/mpesa/withdraw";
      const response = await axios.post(
        `${API}${endpoint}`,
        {
          amount: parseFloat(mpesaAmount),
          currency: mpesaCurrency,
          phone: mpesaPhone,
          description: mpesaOperation === "deposit" ? "OnePay Deposit" : "OnePay Withdrawal"
        },
        { withCredentials: true }
      );
      
      setMpesaSuccess(true);
      toast.success(response.data.message);
      
      // Reset after showing success
      setTimeout(() => {
        setMpesaAmount("");
        setMpesaDialogOpen(false);
        setMpesaSuccess(false);
        fetchData();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "M-Pesa transaction failed");
    } finally {
      setMpesaLoading(false);
    }
  };

  const getCurrencyInfo = (code) => currencies.find((c) => c.code === code);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#14B8A6] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="wallet-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Wallet</h1>
            <p className="text-slate-600 mt-1">Manage your multi-currency balances</p>
          </div>
          <Dialog open={mpesaDialogOpen} onOpenChange={setMpesaDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-[#4CAF50] hover:bg-[#43A047] text-white" data-testid="mpesa-btn">
                <Smartphone className="h-4 w-4 mr-2" />
                M-Pesa
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-[#4CAF50] flex items-center justify-center">
                    <span className="text-white font-bold text-sm">M</span>
                  </div>
                  M-Pesa Transaction
                </DialogTitle>
              </DialogHeader>
              
              {mpesaSuccess ? (
                <div className="py-8 text-center">
                  <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Transaction Successful!</h3>
                  <p className="text-slate-600">
                    Your M-Pesa {mpesaOperation} of {mpesaCurrency} {parseFloat(mpesaAmount).toLocaleString()} has been processed.
                  </p>
                </div>
              ) : (
                <div className="space-y-4 pt-4">
                  {/* M-Pesa Branding */}
                  <div className="p-4 bg-gradient-to-r from-[#4CAF50] to-[#43A047] rounded-xl text-white">
                    <p className="text-sm opacity-90">Safaricom M-Pesa</p>
                    <p className="text-xs opacity-75 mt-1">Instant mobile money transactions</p>
                  </div>

                  <Tabs value={mpesaOperation} onValueChange={setMpesaOperation}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="deposit" className="data-[state=active]:bg-green-100 data-[state=active]:text-green-700" data-testid="mpesa-deposit-tab">
                        <ArrowDownLeft className="h-4 w-4 mr-2" />
                        Deposit
                      </TabsTrigger>
                      <TabsTrigger value="withdraw" className="data-[state=active]:bg-red-100 data-[state=active]:text-red-700" data-testid="mpesa-withdraw-tab">
                        <ArrowUpRight className="h-4 w-4 mr-2" />
                        Withdraw
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>

                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select value={mpesaCurrency} onValueChange={setMpesaCurrency}>
                      <SelectTrigger data-testid="mpesa-currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.filter(c => c.code !== "USD").map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.flag} {c.code} - {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Amount ({mpesaCurrency})</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={mpesaAmount}
                      onChange={(e) => setMpesaAmount(e.target.value)}
                      className="h-12 text-lg"
                      data-testid="mpesa-amount-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>M-Pesa Phone Number</Label>
                    <Input
                      type="tel"
                      placeholder="0712 345 678"
                      value={mpesaPhone}
                      onChange={(e) => setMpesaPhone(e.target.value)}
                      className="h-12"
                      data-testid="mpesa-phone-input"
                    />
                    <p className="text-xs text-slate-500">Enter Kenyan phone number (e.g., 0712345678)</p>
                  </div>

                  {mpesaOperation === "deposit" && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>STK Push:</strong> You will receive an M-Pesa prompt on your phone to authorize this deposit.
                      </p>
                    </div>
                  )}

                  {mpesaOperation === "withdraw" && (
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <strong>B2C Transfer:</strong> Funds will be sent directly to your M-Pesa wallet.
                      </p>
                    </div>
                  )}

                  <Button
                    className={`w-full h-12 rounded-full ${
                      mpesaOperation === "deposit" 
                        ? "bg-[#4CAF50] hover:bg-[#43A047]" 
                        : "bg-red-500 hover:bg-red-600"
                    }`}
                    onClick={handleMpesaTransaction}
                    disabled={mpesaLoading}
                    data-testid="mpesa-submit-btn"
                  >
                    {mpesaLoading ? (
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    ) : mpesaOperation === "deposit" ? (
                      <ArrowDownLeft className="h-4 w-4 mr-2" />
                    ) : (
                      <ArrowUpRight className="h-4 w-4 mr-2" />
                    )}
                    {mpesaOperation === "deposit" ? "Deposit via M-Pesa" : "Withdraw to M-Pesa"}
                  </Button>

                  <p className="text-xs text-center text-slate-500">
                    🔒 Secured by Safaricom M-Pesa (Simulated)
                  </p>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Total Balance Card */}
          <Card className="md:col-span-2 lg:col-span-1 overflow-hidden border-0 shadow-lg" data-testid="total-balance-card">
            <div className="wallet-card-gradient p-6 text-white h-full">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm opacity-80">Total Balance (USD)</span>
                <Wallet className="h-5 w-5 opacity-80" />
              </div>
              <h2 className="text-4xl font-bold">
                ${wallet?.total_usd_value?.toLocaleString("en-US", { minimumFractionDigits: 2 }) || "0.00"}
              </h2>
            </div>
          </Card>

          {/* Currency Balances */}
          {wallet?.balances?.map((balance) => {
            const info = getCurrencyInfo(balance.currency);
            return (
              <Card key={balance.currency} className="border-slate-200" data-testid={`balance-${balance.currency.toLowerCase()}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{info?.flag}</span>
                      <div>
                        <p className="font-medium text-slate-900">{balance.currency}</p>
                        <p className="text-xs text-slate-500">{info?.name}</p>
                      </div>
                    </div>
                    {balance.currency === "KES" && (
                      <Badge className="bg-[#4CAF50] text-white text-xs">M-Pesa</Badge>
                    )}
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {balance.display_amount}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button
            variant="outline"
            className="h-20 flex-col gap-2 rounded-xl border-2 border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50]/10"
            onClick={() => { setMpesaOperation("deposit"); setMpesaDialogOpen(true); }}
            data-testid="quick-mpesa-deposit"
          >
            <ArrowDownLeft className="h-6 w-6" />
            <span className="text-sm font-medium">M-Pesa Deposit</span>
          </Button>
          <Button
            variant="outline"
            className="h-20 flex-col gap-2 rounded-xl border-2 border-red-500 text-red-500 hover:bg-red-50"
            onClick={() => { setMpesaOperation("withdraw"); setMpesaDialogOpen(true); }}
            data-testid="quick-mpesa-withdraw"
          >
            <ArrowUpRight className="h-6 w-6" />
            <span className="text-sm font-medium">M-Pesa Withdraw</span>
          </Button>
          <Button
            variant="outline"
            className="h-20 flex-col gap-2 rounded-xl border-2 border-[#14B8A6] text-[#14B8A6] hover:bg-[#14B8A6]/10"
            onClick={() => document.getElementById("fx-section")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="quick-convert"
          >
            <ArrowLeftRight className="h-6 w-6" />
            <span className="text-sm font-medium">Convert FX</span>
          </Button>
          <Button
            variant="outline"
            className="h-20 flex-col gap-2 rounded-xl border-2 border-slate-300 hover:bg-slate-50"
            data-testid="quick-bank"
          >
            <Plus className="h-6 w-6 text-slate-600" />
            <span className="text-sm font-medium text-slate-600">Bank Transfer</span>
          </Button>
        </div>

        {/* FX Rates & Conversion */}
        <div id="fx-section" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* FX Rates */}
          <Card className="border-slate-200" data-testid="fx-rates-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Current FX Rates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {fxRates.map((rate) => (
                  <div
                    key={rate.currency}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{getCurrencyInfo(rate.currency)?.flag}</span>
                      <span className="font-medium text-slate-900">{rate.currency}</span>
                    </div>
                    <span className="text-slate-600">{rate.display}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-4 text-center">
                Rates powered by Alpha Vantage • Updated hourly
              </p>
            </CardContent>
          </Card>

          {/* FX Conversion */}
          <Card className="border-slate-200" data-testid="fx-conversion-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Convert Currency</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From</Label>
                  <Select value={fxFrom} onValueChange={setFxFrom}>
                    <SelectTrigger data-testid="fx-from-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>To</Label>
                  <Select value={fxTo} onValueChange={setFxTo}>
                    <SelectTrigger data-testid="fx-to-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={fxAmount}
                  onChange={(e) => {
                    setFxAmount(e.target.value);
                    setFxQuote(null);
                  }}
                  className="h-12 text-lg"
                  data-testid="fx-amount-input"
                />
              </div>

              {fxQuote && (
                <div className="p-4 bg-[#14B8A6]/10 rounded-xl space-y-2" data-testid="fx-quote-display">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">You&apos;ll receive</span>
                    <span className="text-lg font-bold text-[#14B8A6]">
                      {getCurrencyInfo(fxTo)?.symbol} {fxQuote.to_amount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <span>Rate</span>
                    <span>1 {fxFrom} = {fxQuote.rate.toFixed(4)} {fxTo}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <span>Spread</span>
                    <span>{fxQuote.spread_bps / 100}%</span>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={getQuote}
                  disabled={fxLoading || !fxAmount}
                  data-testid="get-quote-btn"
                >
                  {fxLoading && !fxQuote ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <ArrowLeftRight className="h-4 w-4 mr-2" />
                  )}
                  Get Quote
                </Button>
                {fxQuote && (
                  <Button
                    className="flex-1 rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                    onClick={executeConversion}
                    disabled={fxLoading}
                    data-testid="convert-btn"
                  >
                    {fxLoading ? (
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Convert
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
